import * as React from 'react'
import {
  Dialog,
  DialogContent,
  Slide,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  DialogActions,
  Button,
  Typography,
  ListItemSecondaryAction,
  Tooltip,
  IconButton,
} from '@material-ui/core'
import { TransitionProps } from '@material-ui/core/transitions'
import { makeStyles } from '@material-ui/core/styles'
import { amber, grey, red } from '@material-ui/core/colors'
import {
  ArrowForwardSharp,
  ErrorRounded,
  WarningRounded,
} from '@material-ui/icons'

import { useFlow } from 'contexts/Flow/flowContext'
import useTranslator from 'utils/hooks/Translator'

export interface Problem {
  type: 'error' | 'warning'
  groupId: string
  blockId: number
  blockName: string
  message: string
}

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 12,
    border: `1px solid ${amber[300]}`,
  },
  titleRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  content: {
    padding: theme.spacing(0, 2, 2, 2),
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${grey[200]}`,
    overflow: 'auto',
    maxHeight: 300,
  },
  listItemWarningIcon: {
    minWidth: 36,
    color: amber[700],
  },
  listItemErrorIcon: {
    minWidth: 36,
    color: red[700],
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(0, 2, 2, 0),
  },
}))

export function WarningModal() {
  const classes = useStyles()
  const { dispatch, currentGroupProblems, goToNodePosition } = useFlow()
  const { getTranslation } = useTranslator()

  const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />
  })

  const handleClose = () => dispatch({ type: 'closeLinterWarningModal' })

  const handleGoToBlock = (blockId: number) => {
    dispatch({ type: 'closeLinterWarningModal' })
    goToNodePosition(blockId)
  }

  return (
    <Dialog
      open
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: classes.paper }}
      aria-labelledby="warnings-title"
    >
      <DialogTitle
        id="warnings-title"
        disableTypography
        className={classes.titleRoot}
      >
        <Typography variant="h5">
          {getTranslation('linter.warnings.modal.title')}
        </Typography>

        <Typography variant="body2">
          {getTranslation('linter.warnings.modal.description')}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Grid item>
          <List dense className={classes.list}>
            {currentGroupProblems.map((problem: Problem, index: number) => (
              <ListItem
                key={problem.blockId}
                style={{
                  backgroundColor: index % 2 === 0 ? grey[50] : 'transparent',
                }}
              >
                {problem.type === 'warning' ? (
                  <ListItemIcon className={classes.listItemWarningIcon}>
                    <WarningRounded />
                  </ListItemIcon>
                ) : (
                  <ListItemIcon className={classes.listItemErrorIcon}>
                    <ErrorRounded />
                  </ListItemIcon>
                )}

                <ListItemText
                  primary={problem.blockName}
                  secondary={problem.message}
                  secondaryTypographyProps={{
                    className: classes.secondaryText,
                  }}
                />

                <ListItemSecondaryAction>
                  <Tooltip
                    title={getTranslation('linter.warnings.modal.goToBlock', {
                      blockId: problem.blockId,
                    })}
                  >
                    <IconButton
                      onClick={() => handleGoToBlock(problem.blockId)}
                    >
                      <ArrowForwardSharp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} variant="outlined">
          {getTranslation('close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
